import { useEffect, useRef, useState, useCallback } from "react";
import { formatearPrecio } from "../../../utils/moneda";
import { obtenerConfiguracionTiendaService } from "../../../services/usuarios_tienda/ConfiguracionTienda";
import {
  getMetodosEnvio,
  getMetodosEnvioId,
  crearMetodoEnvio,
  actualizarMetodoEnvio,
  crearReglaMetodoEnvio,
  actualizarReglaMetodoEnvio,
  eliminarReglaMetodoEnvio,
} from "../../../services/usuarios_tienda/metodos_envio";
import ShippingFormModal from "./ShippingFormModal";
import ShippingRuleModal from "./ShippingRuleModal";
import { IconEdit } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";
import "./ShippingMethodsPage.css";

function ShippingMethodsPage() {
  const [metodos, setMetodos] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [loading, setLoading] = useState(false);
  const [loadingEditMetodo, setLoadingEditMetodo] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedMetodo, setSelectedMetodo] = useState(null);

  const [showRuleModal, setShowRuleModal] = useState(false);
  const [metodoParaReglas, setMetodoParaReglas] = useState(null);
  const [editingRule, setEditingRule] = useState(null);

  const [alert, setAlert] = useState(null);
  const alertTimerRef = useRef(null);
  const [configuracionTienda, setConfiguracionTienda] = useState(null);

  const getErrorMessage = (error, fallback = "Ocurrió un error inesperado.") => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallback
    );
  };

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 3500);
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    obtenerConfiguracionTiendaService().then((res) => {
      if (!alive) return;
      if (res.ok && res.data) setConfiguracionTienda(res.data);
    });
    return () => {
      alive = false;
    };
  }, []);

  const cargarMetodos = useCallback(
    async (currentPage = 1) => {
      try {
        setLoading(true);

        const response = await getMetodosEnvio({
          page: currentPage,
          limit: pageSize,
        });

        setMetodos(Array.isArray(response?.data) ? response.data : []);
        setPagination(
          response?.pagination || {
            page: currentPage,
            limit: pageSize,
            total: 0,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
          }
        );
      } catch (error) {
        console.error("Error cargando métodos de envío:", error);
        showAlert(
          "error",
          getErrorMessage(error, "No se pudieron cargar los métodos de envío.")
        );
        setMetodos([]);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, showAlert]
  );

  useEffect(() => {
    cargarMetodos(page);
  }, [page, cargarMetodos]);

  const cargarMetodoDetalle = async (id) => {
    const raw = await getMetodosEnvioId(id);
    return raw && typeof raw === "object" && "data" in raw && raw.data != null
      ? raw.data
      : raw;
  };

  const openCreateModal = () => {
    setSelectedMetodo(null);
    setShowModal(true);
  };

  const openEditModal = async (metodo) => {
    const id = metodo?.tienda_metodo_envio_id;
    if (!id) {
      showAlert("error", "No se pudo identificar el método de envío.");
      return;
    }

    setLoadingEditMetodo(true);
    try {
      const detalle = await cargarMetodoDetalle(id);

      if (!detalle || typeof detalle !== "object") {
        showAlert("error", "La respuesta del servidor no es válida.");
        return;
      }

      setSelectedMetodo(detalle);
      setShowModal(true);
    } catch (error) {
      console.error("Error cargando método de envío:", error);
      showAlert(
        "error",
        getErrorMessage(error, "No se pudo cargar el método de envío.")
      );
    } finally {
      setLoadingEditMetodo(false);
    }
  };

  const openCreateRuleModal = async (metodo) => {
    const id = metodo?.tienda_metodo_envio_id;
    if (!id) {
      showAlert("error", "No se pudo identificar el método de envío.");
      return;
    }

    try {
      const detalle = await cargarMetodoDetalle(id);

      if (!detalle || typeof detalle !== "object") {
        showAlert("error", "No se pudo cargar el detalle del método.");
        return;
      }

      setMetodoParaReglas(detalle);
      setEditingRule(null);
      setShowRuleModal(true);
    } catch (error) {
      console.error("Error cargando detalle del método para reglas:", error);
      showAlert(
        "error",
        getErrorMessage(error, "No se pudo abrir la configuración de reglas.")
      );
    }
  };

  const openEditRuleModal = async (metodo, rule) => {
    const id = metodo?.tienda_metodo_envio_id;
    if (!id) {
      showAlert("error", "No se pudo identificar el método de envío.");
      return;
    }
    if (!rule?.tienda_metodo_envio_regla_id) {
      showAlert("error", "No se pudo identificar la regla.");
      return;
    }

    try {
      const detalle = await cargarMetodoDetalle(id);

      if (!detalle || typeof detalle !== "object") {
        showAlert("error", "No se pudo cargar el detalle del método.");
        return;
      }

      setMetodoParaReglas(detalle);
      setEditingRule(rule);
      setShowRuleModal(true);
    } catch (error) {
      console.error("Error cargando detalle del método para editar regla:", error);
      showAlert(
        "error",
        getErrorMessage(error, "No se pudo abrir la edición de la regla.")
      );
    }
  };

  const handleDeleteRule = async (rule) => {
    const metodoId = Number(selectedMetodo?.tienda_metodo_envio_id);
    const reglaId = Number(rule?.tienda_metodo_envio_regla_id);

    if (!Number.isInteger(metodoId) || metodoId <= 0) {
      showAlert("error", "No se pudo identificar el método de envío.");
      return;
    }
    if (!Number.isInteger(reglaId) || reglaId <= 0) {
      showAlert("error", "No se pudo identificar la regla.");
      return;
    }

    const zonaNombre =
      String(rule?.zona_nombre || rule?.nombre_zona || "").trim() || "esta zona";

    if (
      !window.confirm(
        `¿Eliminar la regla de envío para ${zonaNombre}? Esta acción no se puede deshacer.`
      )
    ) {
      return;
    }

    try {
      const res = await eliminarReglaMetodoEnvio(metodoId, reglaId);
      showAlert("success", res?.message || "Regla eliminada correctamente.");
      await cargarMetodos(page);
      const detalle = await cargarMetodoDetalle(metodoId);
      setSelectedMetodo(detalle);
    } catch (error) {
      console.error("Error eliminando regla de envío:", error);
      showAlert(
        "error",
        getErrorMessage(error, "No se pudo eliminar la regla de envío.")
      );
    }
  };

  const closeRuleModal = () => {
    setShowRuleModal(false);
    setMetodoParaReglas(null);
    setEditingRule(null);
  };

  const handleCreate = async (data) => {
    try {
      const res = await crearMetodoEnvio(data);
      showAlert(
        "success",
        res?.message || "Método de envío creado correctamente."
      );
      setShowModal(false);
      setPage(1);
      await cargarMetodos(1);
    } catch (error) {
      console.error("Create shipping method failed:", error);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      const res = await actualizarMetodoEnvio(id, data);
      showAlert(
        "success",
        res?.message || "Método de envío actualizado correctamente."
      );
      setShowModal(false);
      await cargarMetodos(page);
    } catch (error) {
      console.error("Update shipping method failed:", error);
      throw error;
    }
  };

  const handleSubmitRule = async (payload) => {
    const metodoId = Number(metodoParaReglas?.tienda_metodo_envio_id);

    if (!Number.isInteger(metodoId) || metodoId <= 0) {
      throw new Error("No se encontró el método de envío.");
    }

    try {
      if (editingRule?.tienda_metodo_envio_regla_id) {
        const res = await actualizarReglaMetodoEnvio(
          metodoId,
          editingRule.tienda_metodo_envio_regla_id,
          payload
        );

        showAlert(
          "success",
          res?.message || "Regla de envío actualizada correctamente."
        );
      } else {
        const res = await crearReglaMetodoEnvio(metodoId, payload);

        showAlert(
          "success",
          res?.message || "Regla de envío creada correctamente."
        );
      }

      await cargarMetodos(page);

      const detalleActualizado = await cargarMetodoDetalle(metodoId);
      setMetodoParaReglas(detalleActualizado);
      setSelectedMetodo((prev) =>
        prev?.tienda_metodo_envio_id === metodoId ? detalleActualizado : prev
      );
      closeRuleModal();
    } catch (error) {
      console.error("Error guardando regla de envío:", error);
      throw new Error(
        getErrorMessage(error, "No se pudo guardar la regla de envío.")
      );
    }
  };

  const nextPage = () => {
    if (pagination.hasNextPage) setPage((p) => p + 1);
  };

  const prevPage = () => {
    if (pagination.hasPreviousPage) setPage((p) => p - 1);
  };

  const goToPage = (n) => {
    const total = pagination.totalPages || 1;
    if (n < 1 || n > total || n === pagination.page || loading) return;
    setPage(n);
  };

  const visiblePages = (() => {
    const total = pagination.totalPages || 1;
    const current = pagination.page || 1;
    const count = 3;
    if (total <= count) return Array.from({ length: total }, (_, i) => i + 1);
    let s = Math.max(1, current - Math.floor(count / 2));
    let e = s + count - 1;
    if (e > total) {
      e = total;
      s = e - count + 1;
    }
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  })();

  const formatCurrency = useCallback(
    (value) =>
      formatearPrecio(value, configuracionTienda, { espacioEntreSimbolo: true }) ||
      "—",
    [configuracionTienda]
  );

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString("es-GT", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  return (
    <div className="tienda-page-scope">
      {alert && (
        <div className={`omx-alert omx-alert--${alert.type}`}>
          <span className="omx-alert__msg">{alert.message}</span>
          <button className="omx-alert__x" onClick={() => setAlert(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="header">
        <div className="methods-header__row">
          <h2 className="title">Métodos de envío</h2>

          {hasPerm("create_shipping_methods") && (
            <button type="button" className="methods-btn-create" onClick={openCreateModal}>
              Crear método
            </button>
          )}
        </div>

        <p className="page-lead">
          Define los métodos de envío de tu tienda, sus costos y reglas por zona.
        </p>
      </div>

      <div className="table-card">
        <div className="table-card-scroll">
        <table className="modern-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre del método</th>
              <th>Costo</th>
              <th>Reglas</th>
              <th>Fecha creado</th>
              <th style={{ textAlign: "right" }}>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="no-data">
                  Cargando métodos de envío...
                </td>
              </tr>
            ) : metodos.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  No se encontraron métodos de envío
                </td>
              </tr>
            ) : (
              metodos.map((m) => (
                <tr key={m.tienda_metodo_envio_id}>
                  <td>{m.tienda_metodo_envio_id}</td>
                  <td>{m.nombre}</td>
                  <td>{formatCurrency(m.costo_base)}</td>
                  <td>{Number(m.total_reglas || 0)}</td>
                  <td>{formatDate(m.fecha_creado)}</td>

                  <td style={{ textAlign: "right" }}>
                      {hasPerm("update_shipping_methods") && (
                        <span
                          className="action-icon edit"
                          onClick={() => !loadingEditMetodo && openEditModal(m)}
                          title={
                            loadingEditMetodo
                              ? "Cargando método..."
                              : "Editar método de envío"
                          }
                          style={
                            loadingEditMetodo
                              ? { opacity: 0.45, pointerEvents: "none" }
                              : undefined
                          }
                        >
                          {IconEdit}
                        </span>
                      )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {showModal && (
        <ShippingFormModal
          isOpen={showModal}
          metodoData={selectedMetodo}
          configuracionTienda={configuracionTienda}
          onClose={() => setShowModal(false)}
          onSubmit={(data) =>
            selectedMetodo
              ? handleUpdate(selectedMetodo.tienda_metodo_envio_id, data)
              : handleCreate(data)
          }
          onManageRules={(metodo) => openCreateRuleModal(metodo)}
          onEditRule={
            hasPerm("update_shipping_methods")
              ? (rule) => openEditRuleModal(selectedMetodo, rule)
              : undefined
          }
          onDeleteRule={
            hasPerm("update_shipping_methods") ? handleDeleteRule : undefined
          }
        />
      )}

      {showRuleModal && metodoParaReglas && (
        <ShippingRuleModal
          isOpen={showRuleModal}
          metodoData={metodoParaReglas}
          ruleData={editingRule}
          configuracionTienda={configuracionTienda}
          onClose={closeRuleModal}
          onSubmit={handleSubmitRule}
        />
      )}

      {(pagination.total > 0 || metodos.length > 0) && (
        <div className="pagination">
          <div className="pagination__center">
            <div className="pagination__nav" aria-label="Paginación de métodos de envío">
              <button
                type="button"
                className="pagination__arrow"
                onClick={prevPage}
                disabled={!pagination.hasPreviousPage || loading}
                aria-label="Página anterior"
              >
                ‹
              </button>

              <div className="pagination__pages">
                {visiblePages.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`pagination__page${n === pagination.page ? " is-active" : ""}`}
                    onClick={() => goToPage(n)}
                    aria-label={`Ir a página ${n}`}
                    aria-current={n === pagination.page ? "page" : undefined}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="pagination__arrow"
                onClick={nextPage}
                disabled={!pagination.hasNextPage || loading}
                aria-label="Página siguiente"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShippingMethodsPage;